import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, MapPin, Bus, Clock, AlertTriangle, RefreshCw } from "lucide-react";

// Public-facing route page — accessible without login. Used by parents
// who scan a QR or click a shared link like:
//   /public/route/702?tenant=<tenant_uuid>
export default function PublicRoute() {
  const { routeId } = useParams();
  const [search] = useSearchParams();
  const tenant = search.get("tenant");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/route/${routeId}/live?tenant=${tenant}`;
      const { data: d } = await axios.get(url);
      setData(d);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!tenant) { setError("Lien public invalide (paramètre 'tenant' manquant)"); return; }
    load(); const i = setInterval(load, 15000); return () => clearInterval(i);
  }, [routeId, tenant]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-zinc-50">
        <div className="bg-white border-2 border-red-500 p-6 max-w-md text-center">
          <AlertTriangle className="mx-auto mb-2 text-red-500" size={32} />
          <div className="font-display font-bold text-lg">Lien indisponible</div>
          <div className="text-sm text-zinc-600 mt-1">{String(error)}</div>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }
  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="public-route-page">
      {/* HEADER (parent-friendly) */}
      <header className="bg-zinc-900 text-white px-4 py-5 border-b-4 border-[#FFC60A]">
        <div className="max-w-2xl mx-auto">
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-[#FFC60A]">LOGIBUS · Suivi parent en direct</div>
          <div className="flex items-center gap-3 mt-1">
            <div className="w-14 h-14 flex items-center justify-center bg-[#FFC60A] border-2 border-[#FFC60A] font-display font-black text-xl text-zinc-900">
              {data.route_short_name}
            </div>
            <div>
              <h1 className="font-display font-black uppercase text-2xl tracking-tighter">{data.route_long_name}</h1>
              <div className="text-xs text-zinc-400">
                Mise à jour {new Date(data.generated_at).toLocaleTimeString("fr-CH")} · actualisation auto 15s
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-4">
        {data.live_buses.length === 0 ? (
          <div className="bg-white border-2 border-zinc-300 p-8 text-center" data-testid="no-bus-message">
            <Bus className="mx-auto mb-3 text-zinc-400" size={36} />
            <div className="font-display font-bold text-lg">Aucun bus en circulation</div>
            <div className="text-sm text-zinc-600 mt-1">Aucun bus n'est actuellement en service sur cette ligne.</div>
          </div>
        ) : data.live_buses.map((b, i) => (
          <BusCard key={i} bus={b} />
        ))}
        <div className="text-center text-[10px] font-mono uppercase tracking-widest text-zinc-400 mt-8">
          LOGIBUS · Transport scolaire intelligent
        </div>
      </main>
    </div>
  );
}

function BusCard({ bus }) {
  const delayColor = bus.delay_min == null ? "text-zinc-500"
    : bus.delay_min <= 2 ? "text-green-700"
    : bus.delay_min <= 5 ? "text-orange-600"
    : "text-red-600";
  return (
    <div className="bg-white border-2 border-zinc-900 shadow-[4px_4px_0_0_#0A0A0B]" data-testid="public-bus-card">
      <div className="bg-[#FFC60A] px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Bus size={20} />
          <span className="font-display font-black text-lg">{bus.bus_label}</span>
        </div>
        {bus.delay_min != null && bus.delay_min !== 0 && (
          <span className={`font-mono font-bold ${delayColor}`}>
            {bus.delay_min > 0 ? `+${bus.delay_min} min de retard` : `${bus.delay_min} min d'avance`}
          </span>
        )}
      </div>
      {bus.headsign && (
        <div className="px-4 py-2 text-sm border-b border-zinc-200">
          <span className="text-zinc-500">Direction :</span> <b>{bus.headsign}</b>
        </div>
      )}
      {bus.next_stop && (
        <div className="px-4 py-3 bg-orange-50 border-b border-zinc-200">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mb-1">
            <MapPin size={11} className="inline" /> Prochain arrêt
          </div>
          <div className="font-display font-bold text-lg">{bus.next_stop}</div>
          {bus.eta_min != null && (
            <div className={`font-mono text-sm mt-0.5 ${delayColor}`}>
              <Clock size={12} className="inline mr-1" /> Arrivée estimée dans <b>{bus.eta_min} min</b>
            </div>
          )}
        </div>
      )}
      {bus.progression?.length > 0 && (
        <div className="px-4 py-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mb-2">Itinéraire</div>
          <ol className="text-sm space-y-1">
            {bus.progression.map((p, idx) => {
              const tone = p.state === "passed" ? "text-zinc-400 line-through"
                : p.state === "current" ? "text-orange-600 font-bold"
                : "text-zinc-800";
              const marker = p.state === "passed" ? "✓" : p.state === "current" ? "🚌" : "○";
              return (
                <li key={idx} className={`flex items-center justify-between ${tone}`} data-testid={`public-stop-${idx}`}>
                  <span className="flex items-center gap-2">
                    <span>{marker}</span>
                    <span className="truncate">{p.stop_name}</span>
                  </span>
                  <span className="font-mono text-[11px]">{p.actual || p.scheduled || "—"}</span>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}
